import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import {
  FileCode,
  Trash2,
  Settings,
  X,
  AlertTriangle,
  Loader2,
  CheckCircle,
  ChevronDown,
} from "lucide-react";
import documentParsingService from "../../../services/document-parsing.service";
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import syntaxHighlight from "../../utils/jsonViewer";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";

import { AvailableVariable } from "../../common/ui/types";

interface Template {
  _id: string;
  schema_name: string;
}

export const ParsingNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [templateId, setTemplateId] = useState<string>(
    data?.schema_id || data?.template_id || "",
  );
  const [summaryField, setSummaryField] = useState<string>(data?.summary || "");
  const [content, setContent] = useState<string>(data?.content || "");
  const [documentStoreId, setDocumentStoreId] = useState<string>(
    data?.document_store_id || "",
  );
  const [fileIds, setFileIds] = useState<string>(data?.file_ids || "");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setDraggedVariable] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { setNodes, deleteElements, getNodes, getEdges } = useReactFlow();

  // Get execution status from node data
  const executionStatus = data?.executionStatus || null;
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const currentNode = useMemo(() => {
    return getNodes().find((n) => n.id === id);
  }, [getNodes, id, data]);

  const validationStatus = useMemo(() => {
    if (!currentNode) return { isValid: true, errors: [] };
    return getNodeValidationStatus(currentNode, getEdges(), getNodes());
  }, [currentNode, getEdges, getNodes]);

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => {
      e.stopPropagation();
    },
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const extractNodeData = useCallback((nodeData: any) => {
    if (!nodeData || typeof nodeData !== "object") {
      return { inputs: {}, outputs: {} };
    }

    const outputs = nodeData.outputs || {};
    const inputs: Record<string, any> = {};
    Object.entries(nodeData).forEach(([key, value]) => {
      if (key !== "outputs") inputs[key] = value;
    });

    return { inputs, outputs };
  }, []);

  const previousNodes = useMemo(() => {
    const nodes = getNodes();
    const edges = getEdges();
    const availableVariables: AvailableVariable[] = [];

    // Helper to find all ancestor nodes using BFS
    const getAllAncestorNodes = (startNodeId: string) => {
      const ancestors = new Map<string, Node>();
      const queue = [startNodeId];
      const visited = new Set<string>([startNodeId]);

      while (queue.length > 0) {
        const currentId = queue.shift()!;
        // Find edges pointing to the current node
        const incomingEdges = edges.filter((edge) => edge.target === currentId);

        for (const edge of incomingEdges) {
          if (!visited.has(edge.source)) {
            const sourceNode = nodes.find((n) => n.id === edge.source);
            if (sourceNode) {
              visited.add(edge.source);
              ancestors.set(edge.source, sourceNode); // Map preserves insertion order (closest first)
              queue.push(edge.source);
            }
          }
        }
      }
      return Array.from(ancestors.values());
    };

    const ancestorNodes = getAllAncestorNodes(id);

    // Sort ancestors by their vertical position (y) to roughly match workflow flow (top to bottom)
    // or keep them as is (closest first). Let's sort by Y to mimic flow order.
    ancestorNodes.sort((a, b) => a.position.y - b.position.y);

    ancestorNodes.forEach((sourceNode) => {
      const { inputs, outputs } = extractNodeData(sourceNode.data);
      availableVariables.push({
        nodeId: sourceNode.id,
        nodeName: sourceNode.data?.name || sourceNode.id,
        nodeType: sourceNode.type || "unknown",
        inputs,
        outputs,
      });
    });

    return availableVariables;
  }, [id, getNodes, getEdges, isModalOpen, extractNodeData]);

  // Check if connected to DOCUMENT-STORE node
  const hasDocumentStoreConnection = useMemo(() => {
    return previousNodes.some((node) => node.nodeType === "DOCUMENT-STORE");
  }, [previousNodes]);

  const handleDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output",
  ) => {
    e.stopPropagation();
    const fullVariable =
      type === "output"
        ? variable
          ? `${nodeName}.outputs.${variable}`
          : `${nodeName}.outputs`
        : variable
          ? `${nodeName}.inputs.${variable}`
          : `${nodeName}.inputs`;
    setDraggedVariable(fullVariable);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", fullVariable);
  };

  const handleDrop = (e: React.DragEvent, fieldType: string) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");

    if (variable) {
      switch (fieldType) {
        case "summary":
          setSummaryField(variable);
          break;
        case "document_store_id":
          setDocumentStoreId(variable);
          break;
        case "file_ids":
          setFileIds(variable);
          break;
        case "content":
          setContent(variable);
          break;
      }
    }
    setDraggedVariable(null);
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const fetchTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await documentParsingService.getTemplatesDropdown();
      if (response.success) setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (templates.length === 0) fetchTemplates();
  }, [templates.length, fetchTemplates]);

  // Auto-populate content field with previous node's content
  useEffect(() => {
    if (!content && !hasDocumentStoreConnection && previousNodes.length > 0) {
      const lastNode = previousNodes[previousNodes.length - 1];
      if (lastNode) {
        setContent(`${lastNode.nodeName}.outputs.content`);
      }
    }
  }, [previousNodes, hasDocumentStoreConnection]);

  const statusConfig = useMemo(() => {
    if (isExecuting) {
      return { bg: "bg-blue-500", text: "Executing...", pulse: true };
    } else if (executionSuccess) {
      return {
        bg: "bg-green-500",
        text: "Executed Successfully",
        pulse: false,
      };
    } else if (executionError) {
      return { bg: "bg-red-500", text: "Execution Failed", pulse: false };
    }

    const hasConfig = hasDocumentStoreConnection
      ? templateId && documentStoreId && fileIds
      : templateId && (summaryField || content);

    return hasConfig
      ? { bg: "bg-emerald-500", text: "Configured", pulse: false }
      : { bg: "bg-gray-400", text: "Not Configured", pulse: false };
  }, [
    templateId,
    summaryField,
    content,
    documentStoreId,
    fileIds,
    hasDocumentStoreConnection,
    isExecuting,
    executionSuccess,
    executionError,
  ]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          const nodeData: any = {
            ...node.data,
            schema_id: templateId,
          };

          if (hasDocumentStoreConnection) {
            // DOCUMENT-STORE mode
            nodeData.document_store_id = documentStoreId;
            nodeData.file_ids = fileIds;
            // Remove summary field if it exists
            delete nodeData.summary;
          } else {
            // Normal mode
            nodeData.summary = summaryField;
            nodeData.content = content;
            // Remove document store fields if they exist
            delete nodeData.document_store_id;
            delete nodeData.file_ids;
          }

          return {
            ...node,
            data: nodeData,
          };
        }
        return node;
      }),
    );
  }, [
    templateId,
    summaryField,
    content,
    documentStoreId,
    fileIds,
    hasDocumentStoreConnection,
    id,
    setNodes,
  ]);

  // Clear outputs when execution starts
  useEffect(() => {
    if (isExecuting) {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                outputs: {}, // Clear outputs
              },
            };
          }
          return node;
        }),
      );
    }
  }, [isExecuting, id, setNodes]);

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;

  return (
    <div
      className={`relative group bg-emerald-50 border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
        !validationStatus.isValid
          ? "border-red-500 shadow-red-200"
          : isExecuting
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : executionSuccess
              ? "border-green-500 shadow-md shadow-green-200"
              : executionError
                ? "border-red-500 shadow-md shadow-red-200"
                : selected
                  ? "border-emerald-500 shadow-md"
                  : "border-emerald-300 hover:border-emerald-300"
      }`}
    >
      {/* Execution Overlay Spinner */}
      {isExecuting && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}

      {/* Success Overlay */}
      {executionSuccess && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {executionError && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-emerald-500 border-2 border-white"
        style={{
          boxShadow:
            "0 0 0 4px rgba(5, 94, 15, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
        }}
      />

      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div
            className={`p-1.5 rounded-lg shadow-sm transition-all ${
              isExecuting
                ? "bg-blue-100 animate-pulse"
                : executionSuccess
                  ? "bg-green-100"
                  : executionError
                    ? "bg-red-100"
                    : "bg-emerald-100"
            }`}
          >
            {isExecuting ? (
              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            ) : (
              <FileCode
                className={`w-4 h-4 ${
                  executionSuccess
                    ? "text-green-700"
                    : executionError
                      ? "text-red-700"
                      : "text-emerald-700"
                }`}
              />
            )}
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">
              Document Parsing
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg} ${
                  statusConfig.pulse ? "animate-pulse" : ""
                }`}
              />
              <p className="text-xs text-gray-600">{statusConfig.text}</p>
            </div>
          </div>
        </div>
        <button
          onClick={handleDeleteClick}
          className="text-gray-500 hover:text-gray-700 p-1 rounded transition"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="mb-3" onClick={stopPropagation}>
        <button
          onClick={handleOpenModal}
          className="w-full py-2.5 rounded-lg bg-emerald-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-emerald-700 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" /> Configure Parsing
        </button>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-emerald-500 border-2 border-white"
        style={{
          boxShadow:
            "0 0 0 4px rgba(5, 94, 15, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
        }}
      />

      {/* Modal */}
      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-50 rounded">
                    <FileCode className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure Document Parsing
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {hasDocumentStoreConnection
                        ? "Document Store mode - Configure store ID and file IDs"
                        : "Standard mode - Configure summary field and template"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Three-column Layout */}
              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* Left Sidebar - Previous Nodes */}
                <div className="w-[340px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleDragStart}
                  />
                </div>

                {/* Center - Main Configuration */}
                <div className="flex-1 overflow-y-auto bg-white p-6">
                  {!validationStatus.isValid && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-red-900 mb-2">
                            Configuration Issues
                          </h4>
                          <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                            {validationStatus.errors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Document Parsing Configuration
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    {hasDocumentStoreConnection
                      ? "Configure for Document Store input"
                      : "Configure summary field and parsing template"}
                  </p>

                  <div className="space-y-5">
                    {/* Conditional Fields based on connection type */}
                    {hasDocumentStoreConnection ? (
                      <>
                        {/* Document Store ID Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Document Store ID{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div
                            onDrop={(e) => handleDrop(e, "document_store_id")}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            className={`min-h-[80px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                              isDraggingOver
                                ? "border-blue-400 bg-blue-50"
                                : documentStoreId
                                  ? "border-blue-300 bg-blue-50/50"
                                  : "border-gray-300 bg-gray-50 hover:border-gray-400"
                            }`}
                          >
                            {documentStoreId ? (
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-500 mb-1">
                                    Selected field
                                  </div>
                                  <code className="text-xs text-blue-700 font-mono break-all">
                                    {documentStoreId}
                                  </code>
                                </div>
                                <button
                                  onClick={() => setDocumentStoreId("")}
                                  className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center text-gray-400">
                                <span className="text-sm">
                                  Drag and drop document_store_id here
                                </span>
                                <span className="text-xs mt-1">
                                  From DOCUMENT-STORE node outputs
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* File IDs Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            File IDs <span className="text-red-500">*</span>
                          </label>
                          <div
                            onDrop={(e) => handleDrop(e, "file_ids")}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            className={`min-h-[80px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                              isDraggingOver
                                ? "border-blue-400 bg-blue-50"
                                : fileIds
                                  ? "border-blue-300 bg-blue-50/50"
                                  : "border-gray-300 bg-gray-50 hover:border-gray-400"
                            }`}
                          >
                            {fileIds ? (
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-500 mb-1">
                                    Selected field
                                  </div>
                                  <code className="text-xs text-blue-700 font-mono break-all">
                                    {fileIds}
                                  </code>
                                </div>
                                <button
                                  onClick={() => setFileIds("")}
                                  className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center text-gray-400">
                                <span className="text-sm">
                                  Drag and drop file_ids here
                                </span>
                                <span className="text-xs mt-1">
                                  From DOCUMENT-STORE node outputs
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Summary Field - Standard Mode */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Summary Field{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div
                            onDrop={(e) => handleDrop(e, "summary")}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            className={`min-h-[80px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                              isDraggingOver
                                ? "border-emerald-400 bg-emerald-50"
                                : summaryField
                                  ? "border-emerald-300 bg-emerald-50/50"
                                  : "border-gray-300 bg-gray-50 hover:border-gray-400"
                            }`}
                          >
                            {summaryField ? (
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-500 mb-1">
                                    Selected field
                                  </div>
                                  <code className="text-xs text-emerald-700 font-mono break-all">
                                    {summaryField}
                                  </code>
                                </div>
                                <button
                                  onClick={() => setSummaryField("")}
                                  className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center text-gray-400">
                                <span className="text-sm">
                                  Drag and drop summary field here
                                </span>
                                <span className="text-xs mt-1">
                                  From OCR, LLM node, or any connected node
                                  outputs
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Content Field - Standard Mode */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content Field
                          </label>
                          <div
                            onDrop={(e) => handleDrop(e, "content")}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            className={`min-h-[80px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                              isDraggingOver
                                ? "border-emerald-400 bg-emerald-50"
                                : content
                                  ? "border-emerald-300 bg-emerald-50/50"
                                  : "border-gray-300 bg-gray-50 hover:border-gray-400"
                            }`}
                          >
                            {content ? (
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-500 mb-1">
                                    Selected field
                                  </div>
                                  <code className="text-xs text-emerald-700 font-mono break-all">
                                    {content}
                                  </code>
                                </div>
                                <button
                                  onClick={() => setContent("")}
                                  className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center text-gray-400">
                                <span className="text-sm">
                                  Drag and drop content field here
                                </span>
                                <span className="text-xs mt-1">
                                  From previous node outputs
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Template Dropdown - Always visible */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template <span className="text-red-500">*</span>
                      </label>
                      {isLoadingTemplates ? (
                        <div className="flex items-center justify-center py-3 bg-white border border-gray-200 rounded-lg">
                          <p className="text-xs text-gray-400">
                            Loading templates...
                          </p>
                        </div>
                      ) : (
                        <div className="relative">
                          <select
                            value={templateId}
                            onChange={(e) => setTemplateId(e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:border-emerald-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition appearance-none cursor-pointer"
                          >
                            <option value="">
                              {templates.length === 0
                                ? "No templates available"
                                : "Select template..."}
                            </option>
                            {templates.map((template) => (
                              <option key={template._id} value={template._id}>
                                {template.schema_name}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Sidebar - Outputs */}
                <div className="w-80 bg-gray-50 overflow-y-auto p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Node Outputs
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Available after execution
                  </p>

                  {hasOutputs && (
                    <div className="mt-4 pt-3 border-t border-emerald-200">
                      <h4 className="text-xs font-semibold text-gray-600 mb-2">
                        JSON Output
                      </h4>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                        <pre className="whitespace-pre-wrap break-words">
                          <code
                            dangerouslySetInnerHTML={{
                              __html: syntaxHighlight(data.outputs),
                            }}
                          />
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  {validationStatus.isValid ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Configuration Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-red-700">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">
                        {validationStatus.errors.length} issue(s) found
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCloseModal}
                    disabled={
                      hasDocumentStoreConnection
                        ? !documentStoreId || !fileIds || !templateId
                        : (!summaryField && !content) || !templateId
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ParsingNode;
